import type { DuplicateCandidate } from "../../../../types/api";
import { Button } from "../../../../components/Button";
import { tourAnchor, type TourAnchorId } from "../../../tours/tourAnchors";
import { matchLabel, memberName, pickBulkSurvivor } from "./duplicateSelection";

interface DuplicateGroupCardProps {
  candidate: DuplicateCandidate;
  selected: boolean;
  onToggleSelect: (candidateId: string) => void;
  onMerge: (candidate: DuplicateCandidate) => void;
  onDismiss: (candidate: DuplicateCandidate) => void;
  /** Set while this group has an action in flight, so both buttons stay disabled. */
  busy?: boolean;
  /** Per-row failure from the last bulk run, reported inline rather than as a toast. */
  error?: string;
  /** The first card in the list, which carries the row-level tour anchors. */
  isFirst?: boolean;
}

export const DuplicateGroupCard = ({
  candidate,
  selected,
  onToggleSelect,
  onMerge,
  onDismiss,
  busy = false,
  error,
  isFirst = false,
}: DuplicateGroupCardProps) => {
  const survivor = pickBulkSurvivor(candidate);
  const checkboxId = `duplicate-select-${candidate.id}`;
  const rowAnchor = (id: TourAnchorId) => (isFirst ? tourAnchor(id) : {});

  return (
    <li className={`duplicates__card${selected ? " duplicates__card--selected" : ""}`}>
      <div className="duplicates__card-header">
        <input
          id={checkboxId}
          type="checkbox"
          className="duplicates__checkbox"
          {...rowAnchor("duplicates.row.select")}
          checked={selected}
          disabled={busy}
          onChange={() => onToggleSelect(candidate.id)}
        />
        <label htmlFor={checkboxId} className="duplicates__card-title">
          {memberName(candidate.members[0] ?? { firstName: "", lastName: "" })}
          <span className="duplicates__card-count">
            {candidate.memberCount} records
          </span>
        </label>

        <div className="duplicates__chips">
          {candidate.matchedOn.map((attribute) => (
            <span key={attribute} className="duplicates__chip">
              {matchLabel(attribute)}
            </span>
          ))}
        </div>

        {/* In the header rather than below the member list: the decision is the
            same one for every group, so it should sit in a fixed place the eye
            can return to instead of moving down the page with the group's size. */}
        <div className="duplicates__card-actions">
          <Button
            variant="secondary"
            disabled={busy}
            {...rowAnchor("duplicates.row.dismiss")}
            onClick={() => onDismiss(candidate)}
          >
            Not duplicates
          </Button>
          <Button
            disabled={busy}
            {...rowAnchor("duplicates.row.merge")}
            onClick={() => onMerge(candidate)}
          >
            Review &amp; merge
          </Button>
        </div>
      </div>

      {candidate.hasTagConflict && (
        <p className="duplicates__warning" role="status">
          These records have different values for the same custom tag. Merging keeps
          the surviving record&apos;s value and drops the others.
        </p>
      )}

      <ul className="duplicates__members">
        {candidate.members.map((member) => (
          <li key={member.prospectId} className="duplicates__member">
            <span className="duplicates__member-name">
              {memberName(member)}
              {member.prospectId === survivor?.prospectId && (
                <span className="duplicates__survivor-badge">Survives</span>
              )}
            </span>
            <span className="duplicates__member-meta">
              {member.gradYear ? `Class of ${member.gradYear}` : "No grad year"}
              {member.position ? ` · ${member.position}` : ""}
              {member.teams.length > 0 ? ` · ${member.teams.join(", ")}` : ""}
            </span>
          </li>
        ))}
      </ul>

      {error && (
        <p className="duplicates__row-error" role="alert">
          {error}
        </p>
      )}
    </li>
  );
};
